import {
    OnboardingAgeAnswer,
    OnboardingExperienceAnswer,
    OnboardingGenderAnswer,
    OnboardingGoalsAnswer,
    OnboardingHowManyRecipesAnswer,
    OnboardingHowOftenAnswer,
    OnboardingMotivationAnswer,
    OnboardingPage,
    OnboardingPageExit,
    OnboardingQuoteAnswer,
    OnboardingRecipeReadyAnswer,
    OnboardingUnitsAnswer,
    OnboardingWhoDoYouKnitMostForAnswer,
    OnboardingYarnAnswer,
    ReboardingLanguageAnswer,
} from '_types/question';

import books from '_assets/img/onboard/bg/books.svg';
import hearts from '_assets/img/onboard/bg/hearts.svg';
//import rocket from '_assets/img/onboard/bg/rocket.svg';
import sheep from '_assets/img/onboard/bg/sheep.svg';
import stars from '_assets/img/onboard/bg/stars.svg';
import gifts from '_assets/img/onboard/bg/three-hearts.svg';
import yarn from '_assets/img/onboard/bg/yarn.svg';
import yarnHeart from '_assets/img/onboard/bg/yarnheart.svg';

import welcome_avif from '_assets/img/onboard/welcome.avif';
import welcome_png from '_assets/img/onboard/welcome.png';
import yay_avif from '_assets/img/onboard/yay.avif';
import yay_png from '_assets/img/onboard/yay.png';
import i18n from '_i18n';
import type { ABVariantsInfo } from '_services/QuestionEngine';
import { createBackgroundImage } from '_utils/backgroundImage';
import type { ABFullVariantId } from 'Pages/Common/AB_tests/AB_test';

export const dummyIllustrations = {
    books,
    hearts,
    sheep,
    stars,
    yarn,
    yarnHeart,
};
export const welcomeQuestion: OnboardingPage = {
    id: 'WELCOME',
    type: 'image',
    text: {
        no: 'Velkommen 🤎<br />En bedre strikkehverdag <i>starter</i> nå.',
        en: 'Welcome 🤎<br />A better knitting life <i>starts</i> now.',
    },
    backgroundImage: createBackgroundImage(welcome_avif, welcome_png),
    nextQuestion: () => {
        return 'KNITTING_MOTIVATION';
    },
};

export const welcomeReferralQuestion: OnboardingPage = {
    id: 'WELCOME_REFERRAL',
    type: 'image',
    text: {
        no: 'Velkommen 🤎<br />Før vi aktiverer prøveperioden vil vi bli litt <i>bedre kjent</i> med deg.',
        en: 'Welcome 🤎<br />Before we activate your free trial, we’d love to <i>get to know you</i> a bit better.',
    },
    backgroundImage: createBackgroundImage(welcome_avif, welcome_png),
    nextQuestion: () => {
        return 'KNITTING_MOTIVATION';
    },
};

export const PROGRESS_BAR_END_VALUE = 1.7;
export const PROGRESS_BAR_END_VALUE_REBOARDING = 1.1;

const knittingMotivationQuestion: OnboardingPage<OnboardingMotivationAnswer> = {
    id: 'KNITTING_MOTIVATION',
    type: 'single_choice',
    header: {
        no: 'Hva motiverer deg mest til å strikke?',
        en: 'What motivates you the most to knit?',
    },
    progress: (isReboarding) => (isReboarding ? 0.3 : 0.1),
    answers: [
        {
            text: {
                no: 'Skape noe eget og unikt',
                en: 'Create something unique',
            },
            value: 'CREATE_UNIQUE',
        },
        {
            text: {
                no: 'Være kreativ',
                en: 'Be creative',
            },
            value: 'BE_CREATIVE',
        },
        {
            text: {
                no: 'Lære nye teknikker og utvikle meg',
                en: 'Learn new techniques and improve',
            },
            value: 'LEARN_NEW_TECHNIQUES',
        },
        {
            text: {
                no: 'Oppleve mestring',
                en: 'Feel a sense of achievement',
            },
            value: 'EXPERIENCE_MASTERY',
        },
        {
            text: {
                no: 'Lage noe til andre',
                en: 'Make something for others',
            },
            value: 'MAKE_SOMETHING_FOR_SOMEONE_ELSE',
        },
        {
            text: {
                no: 'Koble av og finne ro i hverdagen',
                en: 'Unwind and find calm in everyday life',
            },
            value: 'DISCONNECT_AND_EXPERIENCE_PEACE_AND_QUIET',
        },
        {
            text: {
                no: 'Være en del av et fellesskap',
                en: 'Be part of a community',
            },
            value: 'BE_A_PART_OF_THE_COMMUNITY',
        },
    ],
    nextQuestion: (answer) => {
        switch (answer) {
            case 'CREATE_UNIQUE':
            case 'BE_CREATIVE':
                return 'MOTIVATION_1';
            case 'LEARN_NEW_TECHNIQUES':
            case 'EXPERIENCE_MASTERY':
                return 'MOTIVATION_2';
            case 'BE_A_PART_OF_THE_COMMUNITY':
                return 'MOTIVATION_3';
            case 'MAKE_SOMETHING_FOR_SOMEONE_ELSE':
                return 'MOTIVATION_4';
            case 'DISCONNECT_AND_EXPERIENCE_PEACE_AND_QUIET':
                return 'MOTIVATION_5';
            default:
                return 'MOTIVATION_1';
        }
    },
};

export const languageQuestion: OnboardingPage<ReboardingLanguageAnswer> = {
    id: 'LANGUAGE',
    type: 'single_choice',
    header: {
        no: 'La oss starte med hvilket språk du foretrekker? 🤎',
        en: `Let's start with your preferred language 🤎`,
    },
    progress: () => 0.1,
    answers: [
        {
            text: {
                no: 'Norsk',
                en: 'Norwegian',
            },
            value: 'NORWEGIAN',
        },
        {
            text: {
                no: 'Engelsk',
                en: 'English',
            },
            value: 'ENGLISH',
        },
    ],
    nextQuestion: (answer) => {
        switch (answer) {
            case 'NORWEGIAN': {
                i18n.changeLanguage('no');
                break;
            }
            case 'ENGLISH':
            default: {
                i18n.changeLanguage('en');
                break;
            }
        }
        return 'COUNTRY';
    },
};

const countryQuestion: OnboardingPage<ReboardingLanguageAnswer> = {
    id: 'COUNTRY',
    type: 'country',
    header: {
        no: 'Hvilket land bor du i?',
        en: `What country do you live in?`,
    },
    text: {
        en: '',
        no: '',
    },
    progress: () => 0.2,
    nextQuestion: () => {
        return 'KNITTING_MOTIVATION';
    },
};

const motivation1Question: OnboardingPage = {
    id: 'MOTIVATION_1',
    type: 'image',
    illustration: hearts,
    text: {
        no: 'For en <i>fin</i> motivasjon! Dette er akkurat det vi også tenker strikking handler om.',
        en: "What a <i>beautiful</i> motivation! That's exactly what we believe knitting is all about too.",
    },
    nextQuestion: () => {
        return 'GENDER';
    },
};
const motivation2Question: OnboardingPage = {
    id: 'MOTIVATION_2',
    type: 'image',
    illustration: hearts,
    text: {
        no: 'Herlig! Følelsen av å lære seg noe nytt er helt <i>fantastisk,</i> spesielt når du skaper noe med mening.',
        en: "Lovely! Learning something new is an <i>amazing</i> feeling, especially when you're creating something meaningful.",
    },
    nextQuestion: () => {
        return 'GENDER';
    },
};
const motivation3Question: OnboardingPage = {
    id: 'MOTIVATION_3',
    type: 'image',
    illustration: hearts,
    text: {
        no: 'Enig! Strikking blir ekstra meningsfullt når vi <i>deler</i> den med andre.',
        en: 'Agreed! Knitting becomes even more meaningful when we <i>share</i> it with others.',
    },
    nextQuestion: () => {
        return 'GENDER';
    },
};
const motivation4Question: OnboardingPage = {
    id: 'MOTIVATION_4',
    type: 'image',
    illustration: hearts,
    text: {
        no: 'Så fint! Du har mange <i>heldige</i> folk rundt deg.',
        en: "That's so sweet! You've got some <i>lucky</i> people around you.",
    },
    nextQuestion: () => {
        return 'GENDER';
    },
};
const motivation5Question: OnboardingPage = {
    id: 'MOTIVATION_5',
    type: 'image',
    illustration: hearts,
    text: {
        no: 'Den støtter vi! Hvem trenger vel ikke å <i>koble av</i> litt i hverdagen? Da er strikking fantastisk!',
        en: "We totally support that! Who doesn't need to <i>unwind</i> now and then? Knitting really is perfect for that.",
    },
    nextQuestion: () => {
        return 'GENDER';
    },
};
const motivationTextPages = [
    motivation1Question,
    motivation2Question,
    motivation3Question,
    motivation4Question,
    motivation5Question,
];

const unitsQuestion: OnboardingPage<OnboardingUnitsAnswer> = {
    id: 'UNITS',
    type: 'single_choice',
    header: {
        no: 'Velg målesystem',
        en: 'Choose measurement system',
    },
    singleLine: true,
    progress: () => 1.3,
    answers: [
        {
            heading: {
                no: 'Imperial',
                en: 'Imperial',
            },
            text: {
                no: 'oz, inches, yards',
                en: 'oz, inches, yards',
            },
            value: 'IMPERIAL',
        },
        {
            heading: {
                no: 'Metrisk',
                en: 'Metric',
            },
            text: {
                no: 'cm, meter, gram',
                en: 'cm, meter, gram',
            },
            value: 'METRIC',
        },
    ],
    nextQuestion: () => {
        return 'WHO_DO_YOU_KNIT_MOST_FOR';
    },
};

const genderQuestion: OnboardingPage<OnboardingGenderAnswer> = {
    id: 'GENDER',
    progress: (isReboarding) => (isReboarding ? 0.4 : 0.2),
    type: 'single_choice',
    header: {
        no: 'Velg kjønn',
        en: 'Select your gender',
    },
    singleLine: true,
    skipQuestionText: {
        no: 'Ønsker ikke å oppgi',
        en: 'Prefer not to say',
    },
    answers: [
        {
            text: {
                no: 'Annet',
                en: 'Other',
            },
            value: 'OTHER',
        },
        {
            text: {
                no: 'Mann',
                en: 'Male',
            },
            value: 'MAN',
        },
        {
            text: {
                no: 'Kvinne',
                en: 'Female',
            },
            value: 'WOMAN',
        },
    ],
    nextQuestion: () => {
        return 'AGE';
    },
};

const ageQuestion: OnboardingPage<OnboardingAgeAnswer> = {
    id: 'AGE',
    progress: (isReboarding) => (isReboarding ? 0.5 : 0.3),
    type: 'single_choice',
    header: {
        no: 'Hva er alderen din?',
        en: 'What is your age?',
    },
    answers: [
        {
            text: {
                no: 'Under 18',
                en: 'Under 18',
            },
            value: 'UNDER_18',
        },
        {
            text: {
                no: '18 – 24',
                en: '18 – 24',
            },
            value: '18_24',
        },
        {
            text: {
                no: '25 – 34',
                en: '25 – 34',
            },
            value: '25_34',
        },
        {
            text: {
                no: '35 – 44',
                en: '35 – 44',
            },
            value: '35_44',
        },
        {
            text: {
                no: '45 – 54',
                en: '45 – 54',
            },
            value: '45_54',
        },
        {
            text: {
                no: '55 +',
                en: '55 +',
            },
            value: '55_PLUS',
        },
    ],
    nextQuestion: (_, __, isReboarding) => {
        if (isReboarding) {
            return 'EXPERIENCE';
        }
        return 'GOALS';
    },
};

const goalsQuestion: OnboardingPage<OnboardingGoalsAnswer> = {
    id: 'GOALS',
    progress: () => 0.4,
    type: 'multiple_choice',
    maxAnswers: 3,
    header: {
        no: 'Hvilke mål har du?',
        en: 'What are your goals?',
    },
    subHeader: {
        no: 'Velg opptil 3 mål',
        en: 'Choose up to 3 goals',
    },
    answers: [
        {
            text: {
                no: 'Lære nye teknikker',
                en: 'Learn new techniques',
            },
            value: 'LEARN_NEW_TECHNIQUES',
        },
        {
            text: {
                no: 'Oversikt over prosjekter og oppskrifter',
                en: 'Keep track of projects and patterns',
            },
            value: 'GET_BETTER_OVERVIEW_AND_CONTROL',
        },
        {
            text: {
                no: 'Ro og balanse i hverdagen',
                en: 'Find peace and balance in daily life',
            },
            value: 'PEACE_AND_BALANCE_IN_EVERYDAY_LIFE',
        },
        {
            text: {
                no: 'Bruke opp garnet jeg allerede har',
                en: 'Use the yarn I already have',
            },
            value: 'USE_UP_YARN_I_ALREADY_HAVE',
        },
        {
            text: {
                no: 'Strikke til meg selv',
                en: 'Knit for myself',
            },
            value: 'KNIT_FOR_MYSELF',
        },
        {
            text: {
                no: 'Strikke til andre',
                en: 'Knit for others',
            },
            value: 'KNIT_FOR_OTHERS',
        },
        {
            text: {
                no: 'Være mer kreativ',
                en: 'Be more creative',
            },
            value: 'BE_MORE_CREATIVE',
        },
    ],
    nextQuestion: () => {
        return 'EXPERIENCE';
    },
};

const experienceQuestion: OnboardingPage<OnboardingExperienceAnswer> = {
    id: 'EXPERIENCE',
    type: 'single_choice',
    progress: (isReboarding) => (isReboarding ? 0.6 : 0.5),
    header: {
        no: 'Hvor mye erfaring har du?',
        en: 'How much experience do you have?',
    },
    answers: [
        {
            heading: {
                no: 'Helt fersk',
                en: 'Brand new',
            },
            text: {
                no: 'Har såvidt begynt og trenger å lære det meste',
                en: 'Just getting started and still have a lot to learn',
            },
            value: 'FRESH',
        },
        {
            heading: {
                no: 'Litt erfaring',
                en: 'Some experience',
            },
            text: {
                no: 'Kan det grunnleggende og har strikket noen enkle plagg',
                en: 'Know the basics and have knitted a few simple pieces',
            },
            value: 'SOME_EXPERIENCE',
        },
        {
            heading: {
                no: 'Ganske erfaren',
                en: 'Quite experienced',
            },
            text: {
                no: 'Har strikket mye, men anser meg ikke som noen ekspert',
                en: "Have knitted a lot, but wouldn't call myself an expert",
            },
            value: 'QUITE_EXPERIENCED',
        },
        {
            heading: {
                no: 'Svært erfaren',
                en: 'Very experienced',
            },
            text: {
                no: 'Har strikket i mange år og kan enkelt tilpasse oppskrifter etter eget ønske',
                en: 'Knitted for years and can easily adapt patterns to my own preferences',
            },
            value: 'VERY_EXPERIENCED',
        },
    ],
    nextQuestion: (answer) => {
        switch (answer) {
            case 'FRESH':
                return 'NO_PROBLEM';
            case 'SOME_EXPERIENCE':
                return 'HOW_DELIGHTFUL';
            case 'QUITE_EXPERIENCED':
                return 'A_BETTER_DAY_STARTS_NOW';
            case 'VERY_EXPERIENCED':
                return 'TOTALLY_AWESOME';
            default:
                return 'NO_PROBLEM';
        }
    },
};

const noProblemQuestion: OnboardingPage = {
    id: 'NO_PROBLEM',
    type: 'image',
    text: (isReboarding) => {
        if (isReboarding) {
            return {
                no: 'Alle begynner et sted 💛 Kanskje du har oppdaget hjelpevideoene og oppskriftene for <i>nybegynnere?</i>',
                en: 'Everyone starts somewhere 💛 Maybe you’ve discovered the <i>beginner-friendly</i> video tutorials and patterns?',
            };
        }
        return {
            no: 'Ikke noe problem - <i>we got you</i> 🥰 Nå får du masse oppskrifter, verktøy og videoer som hjelper deg skikkelig i gang!',
            en: "No problem - <i>we got you</i> 🥰 Now you'll get lots of patterns, tools, and videos to help you get going!",
        };
    },
    illustration: stars,
    nextQuestion: () => {
        return 'HOW_OFTEN';
    },
};

const howDelightfulQuestion: OnboardingPage = {
    id: 'HOW_DELIGHTFUL',
    type: 'image',
    text: (isReboarding) => {
        if (isReboarding) {
            return {
                no: 'Så gøy 😍 Da kan du det grunnleggende, så er vi her for å hjelpe deg med <i>neste steg.</i>',
                en: 'That’s great 😍 You’ve got the basics down and we’re here to help you take the <i>next step.</i>',
            };
        }
        return {
            no: 'Så gøy at du er i gang 😍 Nå får du alt du trenger for å lære, vokse og bli en <i>enda bedre</i> strikker.',
            en: "You're off to a great start 😍 Now you'll get everything you need to learn, grow, and become an <i>even better</i> knitter.",
        };
    },
    illustration: stars,
    nextQuestion: () => {
        return 'HOW_OFTEN';
    },
};

const betterDaystartsNowQuestion: OnboardingPage = {
    id: 'A_BETTER_DAY_STARTS_NOW',
    type: 'image',
    text: (isReboarding) => {
        if (isReboarding) {
            return {
                no: 'Perfekt 😍 Kanskje det likevel er noen verktøy eller oppskrifter du ikke har <i>testet</i> ennå?',
                en: 'Perfect 😍 Maybe there are still some tools or patterns you haven’t <i>tried</i> yet?',
            };
        }
        return {
            no: 'Da er du på rett plass 😍 Nå får du alt du trenger <i>samlet</i> på et sted, så du kan fokusere på strikkingen.',
            en: "Then you're in the right place 😍 Now you'll get everything you need in <i>one place,</i> so you can focus on the knitting.",
        };
    },
    illustration: stars,
    nextQuestion: () => {
        return 'HOW_OFTEN';
    },
};

const totallyAwesomeQuestion: OnboardingPage = {
    id: 'TOTALLY_AWESOME',
    type: 'image',
    text: (isReboarding) => {
        if (isReboarding) {
            return {
                no: 'Perfekt 😍 Vi er glade for å få være med på å holde <i>orden</i> i strikkelivet ditt!',
                en: `Perfect 😍 We're so happy to be part of keeping your knitting life in <i>order!</i>`,
            };
        }
        return {
            no: 'Da er du på rett plass 😍 Nå får du masse ny inspirasjon og smarte verktøy som gjør det <i>enkelt</i> å holde styr på prosjektene dine.',
            en: "Then you're in the right place 😍 Now you'll get lots of new inspiration and smart tools that make it <i>easy</i> to keep track of your projects.",
        };
    },
    illustration: stars,
    nextQuestion: () => {
        return 'HOW_OFTEN';
    },
};

const howOftenQuestion: OnboardingPage<OnboardingHowOftenAnswer> = {
    id: 'HOW_OFTEN',
    type: 'single_choice',
    progress: (isReboarding) => (isReboarding ? 0.7 : 0.6),
    header: {
        no: 'Hvor ofte strikker du?',
        en: 'How often do you knit?',
    },
    answers: [
        {
            text: {
                no: 'Hver dag',
                en: 'Every day',
            },
            value: 'EVERY_DAY',
        },
        {
            text: {
                no: 'Flere ganger i uken',
                en: 'Several times a week',
            },
            value: 'SEVERAL_TIMES_A_WEEK',
        },
        {
            text: {
                no: 'Noen ganger i måneden',
                en: 'A few times a month',
            },
            value: 'FEW_TIMES_A_MONTH',
        },
        {
            text: {
                no: 'Sjeldnere',
                en: 'Less often',
            },
            value: 'RARELY',
        },
    ],
    nextQuestion: (answer) => {
        switch (answer) {
            case 'EVERY_DAY':
            case 'SEVERAL_TIMES_A_WEEK':
                return 'SUPER_OFTEN';
            case 'FEW_TIMES_A_MONTH':
            case 'RARELY':
                return 'WHEN_YOURE_READY_OFTEN';
            default:
                return 'SUPER_OFTEN';
        }
    },
};

const superOftenQuestion: OnboardingPage = {
    id: 'SUPER_OFTEN',
    type: 'image',
    text: {
        no: '<i>Imponerende!</i><br />Det kan vi like 😍',
        en: '<i>Impressive!</i><br />We love that 😍',
    },
    illustration: sheep,
    nextQuestion: (_, __, isReboarding) => {
        if (isReboarding) return 'WHO_DO_YOU_KNIT_MOST_FOR';
        return 'HOW_MANY_RECIPES';
    },
};

const whenYoureReadyOftenQuestion: OnboardingPage = {
    id: 'WHEN_YOURE_READY_OFTEN',
    type: 'image',
    text: (isReboarding) => {
        if (isReboarding) {
            return {
                no: 'Sånn er det for mange 🤎<br />Vi er så glad for at vi kan hjelpe deg å ta opp tråden når <i>strikkelysten</i> melder seg.',
                en: `That’s the case for many 🤎<br />We’re glad to help you pick up the thread whenever the <i>knitting mood</i> strikes.`,
            };
        }
        return {
            no: 'Når du er klar, er vi det også 🤗<br />Hver gang strikkelysten melder seg, ligger <i>alt klart</i> – akkurat der du slapp.',
            en: "When you're ready, so are we 🤗<br />Every time the knitting urge strikes, everything is <i>ready</i> – right where you left off.",
        };
    },
    illustration: sheep,
    nextQuestion: (_, __, isReboarding) => {
        if (isReboarding) return 'WHO_DO_YOU_KNIT_MOST_FOR';
        return 'HOW_MANY_RECIPES';
    },
};

const howManyRecipesQuestion: OnboardingPage<OnboardingHowManyRecipesAnswer> = {
    id: 'HOW_MANY_RECIPES',
    progress: () => 0.7,
    type: 'single_choice',
    header: {
        no: 'Har du mange oppskrifter fra før?',
        en: 'Do you have many patterns already?',
    },
    answers: [
        {
            text: {
                no: 'Ja',
                en: 'Yes',
            },
            value: 'YES',
        },
        {
            text: {
                no: 'Nei',
                en: 'No',
            },
            value: 'NO',
        },
        {
            text: {
                no: 'Noen få',
                en: 'Just a few',
            },
            value: 'A_FEW',
        },
    ],
    nextQuestion: () => {
        return 'DO_YOU_HAVE_A_RECIPE_READY_TO_GO';
    },
};

const doYouHaveARecipeReadyToGoQuestion: OnboardingPage<OnboardingRecipeReadyAnswer> =
    {
        id: 'DO_YOU_HAVE_A_RECIPE_READY_TO_GO',
        type: 'single_choice',
        progress: () => 0.8,
        header: {
            no: 'Har du en oppskrift du vil bruke med en gang?',
            en: 'Do you have a pattern you want to use right away?',
        },
        answers: [
            {
                text: {
                    no: 'Ja',
                    en: 'Yes',
                },
                value: 'YES',
            },
            {
                text: {
                    no: 'Nei',
                    en: 'No',
                },
                value: 'NO',
            },
        ],
        nextQuestion: (answer) => {
            switch (answer) {
                case 'YES':
                    return 'RECIPE_YES';
                case 'NO':
                default:
                    return 'RECIPE_NO';
            }
        },
    };
const recipeNoQuestion: OnboardingPage = {
    id: 'RECIPE_NO',
    type: 'image',
    text: {
        no: 'Ingen stress! 🤗<br />Nå får du mange <i>oppskrifter</i> som bare venter på å bli strikket.',
        en: "No stress! 🤗<br />Now you'll get lots of <i>patterns</i> just waiting to be knitted.",
    },
    illustration: books,
    nextQuestion: () => {
        return 'DO_YOU_HAVE_A_LOT_OF_YARN';
    },
};

const recipeYesQuestion: OnboardingPage = {
    id: 'RECIPE_YES',
    type: 'image',
    text: {
        no: 'Perfekt!<br />Last den opp så kan du bruke den med markeringer, omgangsteller og andre smarte verktøy som gjør strikkingen mer <i>smooth</i> 😎 ',
        en: 'Perfect!<br />Just upload it to use it with markers, row counter, and other smart tools that make your knitting <i>smoother</i> 😎',
    },
    illustration: books,
    nextQuestion: () => {
        return 'DO_YOU_HAVE_A_LOT_OF_YARN';
    },
};

const doYouHaveALotOfYarnQuestion: OnboardingPage<OnboardingYarnAnswer> = {
    id: 'DO_YOU_HAVE_A_LOT_OF_YARN',
    progress: () => 0.9,
    type: 'single_choice',
    header: {
        no: 'Har du mye garn?',
        en: 'Do you have a lot of yarn?',
    },
    answers: [
        {
            text: {
                no: 'Ja, mye 🥵',
                en: 'Yes, a lot 🥵',
            },
            value: 'A_LOT',
        },
        {
            text: {
                no: 'Nok til et par prosjekter',
                en: 'Enough for a couple of projects',
            },
            value: 'SOME',
        },
        {
            text: {
                no: 'Kun restegarn',
                en: 'Just scrap yarn',
            },
            value: 'LEFTOVERS',
        },
        {
            text: {
                no: 'Ingenting',
                en: 'Nothing',
            },
            value: 'NOTHING',
        },
    ],
    nextQuestion: (answer) => {
        switch (answer) {
            case 'A_LOT':
                return 'YARN_LOTS';
            case 'SOME':
                return 'YARN_SOME';
            case 'LEFTOVERS':
            case 'NOTHING':
            default:
                return 'YARN_NOTHING';
        }
    },
};

const yarnYesQuestion: OnboardingPage = {
    id: 'YARN_LOTS',
    type: 'image',
    text: {
        no: 'We can relate 🤭<br />Derfor har vi laget garnlageret! Det oppdateres automatisk så du alltid har <i>kontroll</i> på hva du har.',
        en: "We can relate 🤭<br />That's why we made the yarn stash! It updates automatically so you always know <i>exactly</i> what you have.",
    },
    illustration: yarn,
    nextQuestion: (_, previousAnswers) => {
        const isNotExperienced =
            previousAnswers.includes('FRESH') ||
            previousAnswers.includes('SOME_EXPERIENCE');
        return isNotExperienced ? 'QUOTE_FRESH_1' : 'QUOTE_EXPERIENCE_1';
    },
};

const yarnSomeQuestion: OnboardingPage = {
    id: 'YARN_SOME',
    type: 'image',
    text: {
        no: 'Da vil garnlageret være nyttig!<br />Det oppdateres automatisk så du alltid har <i>kontroll</i> på hva du har.',
        en: 'Then the yarn stash will be useful! It updates automatically so you always know <i>exactly</i> what you have.',
    },
    illustration: yarn,
    nextQuestion: (_, previousAnswers) => {
        const isNotExperienced =
            previousAnswers.includes('FRESH') ||
            previousAnswers.includes('SOME_EXPERIENCE');
        return isNotExperienced ? 'QUOTE_FRESH_1' : 'QUOTE_EXPERIENCE_1';
    },
};
const yarnNoQuestion: OnboardingPage = {
    id: 'YARN_NOTHING',
    type: 'image',
    text: {
        no: 'No worries ☺️ Vi har <i>alt</i> du trenger til ditt neste prosjekt i appen.',
        en: 'No worries ☺️ We have <i>everything</i> you need for your next project in the app.',
    },
    illustration: yarn,
    nextQuestion: (_, previousAnswers) => {
        const isNotExperienced =
            previousAnswers.includes('FRESH') ||
            previousAnswers.includes('SOME_EXPERIENCE');
        return isNotExperienced ? 'QUOTE_FRESH_1' : 'QUOTE_EXPERIENCE_1';
    },
};

const quoteFresh1Question: OnboardingPage<OnboardingQuoteAnswer> = {
    id: 'QUOTE_FRESH_1',
    type: 'quote',
    color: 'pale-green',
    progress: () => 1,
    header: {
        no: 'Kan du relatere til denne påstanden?',
        en: 'Can you relate to this statement?',
    },
    text: {
        no: 'Jeg kan miste motivasjonen når jeg står fast, gjør feil eller ikke forstår teknikkene',
        en: "I can lose motivation when I get stuck, make mistakes, or don't understand the techniques",
    },
    answers: [
        { text: { no: 'Nei', en: 'No' }, value: 'NO' },
        { text: { no: 'Ja', en: 'Yes' }, value: 'YES' },
    ],
    nextQuestion: () => {
        return 'QUOTE_FRESH_2';
    },
};

const quoteFresh2Question: OnboardingPage<OnboardingQuoteAnswer> = {
    id: 'QUOTE_FRESH_2',
    type: 'quote',
    color: 'soft-rose',
    progress: () => 1.1,
    header: {
        no: 'Kan du relatere til denne påstanden?',
        en: 'Can you relate to this statement?',
    },
    text: {
        no: 'Jeg kan synes det er vanskelig å finne gode og enkle oppskrifter og riktig garn',
        en: 'I sometimes find it hard to find good and simple patterns and the right yarn',
    },
    answers: [
        { text: { no: 'Nei', en: 'No' }, value: 'NO' },
        { text: { no: 'Ja', en: 'Yes' }, value: 'YES' },
    ],
    nextQuestion: () => {
        return 'QUOTE_FRESH_3';
    },
};

const quoteFresh3Question: OnboardingPage<OnboardingQuoteAnswer> = {
    id: 'QUOTE_FRESH_3',
    type: 'quote',
    color: 'banana',
    progress: () => 1.2,
    header: {
        no: 'Kan du relatere til denne påstanden?',
        en: 'Can you relate to this statement?',
    },
    text: {
        no: 'Jeg kan miste oversikten og glemme hvor jeg er i oppskriften',
        en: 'I can lose track and forget where I am in the pattern.',
    },
    answers: [
        { text: { no: 'Nei', en: 'No' }, value: 'NO' },
        { text: { no: 'Ja', en: 'Yes' }, value: 'YES' },
    ],
    nextQuestion: () => {
        return 'UNITS';
    },
};

const quoteExperience1Question: OnboardingPage<OnboardingQuoteAnswer> = {
    id: 'QUOTE_EXPERIENCE_1',
    progress: () => 1,
    type: 'quote',
    color: 'pale-green',
    header: {
        no: 'Kan du relatere til denne påstanden?',
        en: 'Can you relate to this statement?',
    },
    text: {
        no: 'Oppskrifter og notater havner litt her og der, og det kan bli vanskelig å finne tilbake',
        en: 'Patterns and notes end up a bit here and there, and it can be hard to find them again',
    },
    answers: [
        { text: { no: 'Nei', en: 'No' }, value: 'NO' },
        { text: { no: 'Ja', en: 'Yes' }, value: 'YES' },
    ],
    nextQuestion: () => {
        return 'QUOTE_EXPERIENCE_2';
    },
};

const quoteExperience2Question: OnboardingPage<OnboardingQuoteAnswer> = {
    id: 'QUOTE_EXPERIENCE_2',
    progress: () => 1.1,
    type: 'quote',
    color: 'soft-rose',
    header: {
        no: 'Kan du relatere til denne påstanden?',
        en: 'Can you relate to this statement?',
    },
    text: {
        no: 'Det gir en god følelse å se tilbake på alle prosjektene jeg har strikket',
        en: "It feels great to look back at all the projects I've knitted",
    },
    answers: [
        { text: { no: 'Nei', en: 'No' }, value: 'NO' },
        { text: { no: 'Ja', en: 'Yes' }, value: 'YES' },
    ],
    nextQuestion: () => {
        return 'QUOTE_EXPERIENCE_3';
    },
};

const quoteExperience3Question: OnboardingPage<OnboardingQuoteAnswer> = {
    id: 'QUOTE_EXPERIENCE_3',
    progress: () => 1.2,
    type: 'quote',
    color: 'banana',
    header: {
        no: 'Kan du relatere til denne påstanden?',
        en: 'Can you relate to this statement?',
    },
    text: {
        no: 'Jeg skulle ønske jeg hadde bedre oversikt over prosjekter, oppskrifter og garn.',
        en: 'I wish I had better overview of my projects, patterns, and yarn',
    },
    answers: [
        { text: { no: 'Nei', en: 'No' }, value: 'NO' },
        { text: { no: 'Ja', en: 'Yes' }, value: 'YES' },
    ],
    nextQuestion: () => {
        return 'UNITS';
    },
};

const whoDoYouKnitMostForQuestion: OnboardingPage<OnboardingWhoDoYouKnitMostForAnswer> =
    {
        id: 'WHO_DO_YOU_KNIT_MOST_FOR',
        type: 'multiple_choice',
        progress: (isReboarding) => (isReboarding ? 0.8 : 1.4),
        maxAnswers: 4,
        header: (isReboarding) => {
            if (isReboarding) {
                return {
                    no: 'Hvem strikker du oftest til?',
                    en: 'Who do you usually knit for?',
                };
            }
            return {
                no: 'Hvem vil du strikke til?',
                en: 'Who do you want to knit for?',
            };
        },
        answers: [
            {
                text: {
                    no: 'Dame',
                    en: 'Women',
                },
                value: 'WOMEN',
            },
            {
                text: {
                    no: 'Herre',
                    en: 'Men',
                },
                value: 'MEN',
            },
            {
                text: {
                    no: 'Barn',
                    en: 'Junior',
                },
                value: 'CHILDREN',
            },
            {
                text: {
                    no: 'Baby',
                    en: 'Baby',
                },
                value: 'BABY',
            },
        ],
        nextQuestion: () => {
            return 'WHAT_DO_YOU_KNIT_THE_MOST';
        },
    };

const whatDoYouKnitMostForQuestion: OnboardingPage = {
    id: 'WHAT_DO_YOU_KNIT_THE_MOST',
    type: 'category',
    header: (isReboarding) => {
        if (isReboarding) {
            return {
                no: 'Hva liker du best å strikke?',
                en: 'What do you enjoy knitting the most?',
            };
        }
        return {
            no: 'Hva har du mest lyst til å strikke?',
            en: 'What would you most like to knit?',
        };
    },
    text: {
        no: 'Velg så mange du vil',
        en: 'Choose as many as you want',
    },
    progress: (isReboarding) => (isReboarding ? 0.8 : 1.5),
    nextQuestion: () => {
        return 'CHOOSE_3_PATTERNS';
    },
};

const choose3PatternsQuestion: OnboardingPage = {
    id: 'CHOOSE_3_PATTERNS',
    type: 'pattern',
    progress: (isReboarding) => (isReboarding ? 1.0 : 1.6),
    header: {
        no: 'Velg minst 3 oppskrifter du liker',
        en: 'Choose at least 3 patterns you like',
    },
    nextQuestion: () => {
        return 'PATTERN_CONFIRMATION';
    },
};

const patternConfirmationQuestion: OnboardingPage = {
    id: 'PATTERN_CONFIRMATION',
    type: 'image',
    progress: (isReboarding) => (isReboarding ? 1.1 : 1.7),
    text: (isReboarding) => {
        if (isReboarding) {
            return {
                no: 'De var fine ❤️ Vi har <i>lagret</i> dem på din nye hjem-skjerm, der vi også har lagt til flere nye snarveier.',
                en: 'Nice choices ❤️ We’ve <i>saved</i> them to your new home screen, where we’ve also added more shortcuts.',
            };
        }
        return {
            no: 'De var fine! Disse og hundrevis av andre oppskrifter <i>venter</i> på deg i appen.',
            en: 'Nice choices! These and hundreds of other patterns are <i>waiting</i> for you in the app.',
        };
    },
    illustration: yarnHeart,
    adjustIllustrationUp: true,
    nextQuestion: (_, __, isReboarding) => {
        if (isReboarding) return 'SO_FUN';
        return 'EXIT';
    },
};

// *****************************************************************************************************************

const soFunQuestion: OnboardingPage = {
    id: 'SO_FUN',
    type: 'image',
    text: (_, isPremium) => {
        if (isPremium)
            return {
                no: 'Så gøy å bli litt bedre kjent med deg 🥰 Svarene dine hjelper oss å gjøre appen mer <i>personlig</i> og relevant for deg ✨',
                en: 'So fun getting to know you a little better 🥰  Your answers help us make the app more <i>personal</i> and relevant to you ✨',
            };
        else
            return {
                no: 'Så gøy å bli litt bedre kjent med deg! La oss gå videre 🤎 ',
                en: 'So fun getting to know you a little better! Let’s move on 🤎',
            };
    },
    illustration: gifts,

    buttonText: (_, isPremium) =>
        isPremium
            ? {
                  en: 'To home screen',
                  no: 'Til hjem-skjerm',
              }
            : undefined,
    nextQuestion: () => {
        return 'EXIT';
    },
};

export const yayQuestion: OnboardingPage = {
    id: 'YAY',
    type: 'image',
    text: {
        no: 'YAY 🥳<br />Du er nå sammen med strikkere fra hele verden som <i>elsker</i> å skape, inspirere og bli inspirert.',
        en: 'YAY 🥳<br />You’re now joining knitters from all over the world who <i>love</i> to create, inspire, and be inspired.',
    },
    backgroundImage: createBackgroundImage(yay_avif, yay_png),
    buttonText: {
        no: 'Kom i gang',
        en: 'Get started',
    },
    nextQuestion: () => {
        return 'EXIT';
    },
};

const exitQuestion: OnboardingPageExit = {
    id: 'EXIT',
    type: 'exit',
    header: {
        no: '',
        en: '',
    },
    nextQuestion: () => {
        return 'EXIT';
    },
};

export const TRIAL_TIMELINE_CONTENT = (reminderAt = 10, paymentAt = 14) => ({
    day1: {
        no: 'I dag',
        en: 'Today',
    },
    day1Text: {
        no: 'Ingen betaling. Du starter i dag.',
        en: 'No payment. You start today.',
    },
    dayReminder: {
        no: `Dag ${reminderAt}`,
        en: `Day ${reminderAt}`,
    },
    dayReminderText: {
        no: 'Du får en påminnelse på e-post i god tid før prøveperioden din utløper.',
        en: 'You’ll get an email reminder well before your trial ends.',
    },
    dayPayment: {
        no: `Dag ${paymentAt}`,
        en: `Day ${paymentAt}`,
    },
    dayPaymentText: {
        no: 'Betaling gjennomføres.<br />Avslutt før hvis du ikke er fornøyd.',
        en: 'Payment will be processed.<br />Cancel in advance if you’re not happy.',
    },
    continueButton: {
        no: 'Fortsett',
        en: 'Continue',
    },
    // use for referrals
    conditions: {
        no: 'Betingelser og vilkår',
        en: 'Terms and conditions',
    },
    startTrialButton: {
        no: 'Start prøveperiode',
        en: 'Start trial',
    },
});
export const questionTree: OnboardingPage[] = [
    welcomeQuestion,
    welcomeReferralQuestion,
    knittingMotivationQuestion,
    languageQuestion,
    countryQuestion,
    ...motivationTextPages,
    unitsQuestion,
    genderQuestion,
    ageQuestion,
    goalsQuestion,
    experienceQuestion,
    noProblemQuestion,
    howDelightfulQuestion,
    betterDaystartsNowQuestion,
    totallyAwesomeQuestion,
    howOftenQuestion,
    superOftenQuestion,
    whenYoureReadyOftenQuestion,
    howManyRecipesQuestion,
    doYouHaveARecipeReadyToGoQuestion,
    recipeNoQuestion,
    recipeYesQuestion,
    doYouHaveALotOfYarnQuestion,
    yarnYesQuestion,
    yarnSomeQuestion,
    yarnNoQuestion,
    quoteFresh1Question,
    quoteFresh2Question,
    quoteFresh3Question,
    quoteExperience1Question,
    quoteExperience2Question,
    quoteExperience3Question,
    whoDoYouKnitMostForQuestion,
    whatDoYouKnitMostForQuestion,
    choose3PatternsQuestion,
    patternConfirmationQuestion,
    soFunQuestion,
    exitQuestion,
    yayQuestion,
];

type ABQuestionReplacement = { id_in: string; replacement: string }[];
type ABAlternatives<TVariantId extends string = ABFullVariantId> = Partial<
    Record<TVariantId, ABQuestionReplacement>
>;

const ab_alternatives: ABAlternatives = {
    // control alternative is the same, but must be set for logging.
    /*  
        Kept here for documentation of pattern. Remove when new test arrives.
        
        'feb26_01.control': [
            {
                id_in: patternConfirmationQuestion.id,
                replacement: patternConfirmationQuestion.id,
            },
        ],
        'feb26_01.a': [
            {
                id_in: patternConfirmationQuestion.id,
                replacement: patternConfirmationQuestion_ab_var_feb.id,
            },
        ],
        'feb26_01.b': [
            {
                id_in: patternConfirmationQuestion.id,
                replacement: patternConfirmationQuestion_ab_var_feb.id,
            },
           
        ],*/
};

/** AB tests replace question ids for alternative flows. */
export const findAB_id_variantForQuestion = <
    TVariantId extends string = ABFullVariantId,
>(
    questionId: string,
    ab_variants: ABVariantsInfo<TVariantId>,
    ab_alts: ABAlternatives<TVariantId> = ab_alternatives as ABAlternatives<TVariantId>,
): string | undefined => {
    let replacmentQuestionId: string | undefined;
    for (const variantId of Object.keys(ab_variants)) {
        if (!variantId) {
            continue;
        }
        const alt = ab_alts[variantId];
        if (alt) {
            const ab_alternative_question = alt.find(
                (a) => a.id_in === questionId,
            )?.replacement;
            if (ab_alternative_question) {
                if (replacmentQuestionId) {
                    if (process.env.HOST?.includes('stage')) {
                        throw new Error(
                            `Multiple AB variants found for question ${questionId}. This should not happen and can cause unexpected behavior. Please check the AB alternatives configuration.`,
                        );
                    } else {
                        console.warn(
                            `Multiple AB variants found for question ${questionId}. This should not happen and can cause unexpected behavior. Please check the AB alternatives configuration.`,
                        );
                    }
                }

                replacmentQuestionId = ab_alternative_question;
                ab_variants[variantId]?.logExposure();
            }
        }
    }

    return replacmentQuestionId;
};

export const getQuestionById = (
    id: string,
    ab_variants?: ABVariantsInfo,
): OnboardingPage | undefined => {
    let idToFind = id;

    // Check if AB testing overrides flow.
    const abIdVariant = ab_variants
        ? findAB_id_variantForQuestion(id, ab_variants)
        : undefined;
    if (abIdVariant) {
        idToFind = abIdVariant;
    }

    const question = questionTree.find((q) => q.id === idToFind);
    return question;
};
